import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useReducer, useRef } from "react";
import { toast } from "sonner";
import { type TicketDetailDto, type TicketDto, type TicketResolutionInput, TicketStatus, useTicketApi } from "@/api/tickets";
import { useI18n } from "@/locales/i18n-provider";
import { RequestError } from "@/request";
import { activeResolutionSession, ticketResolutionSessionReducer } from "../model/ticket-resolution-session";

type ResolveRequest = {
  sessionId: number;
  ticketId: string;
  concurrencyStamp: string;
  input: TicketResolutionInput;
};

/** 所有解决入口共用一次写入；状态与解决记录由服务端一起保存。 */
export function useTicketResolution(scopeKey?: string) {
  const { t } = useI18n();
  const ticketApi = useTicketApi();
  const queryClient = useQueryClient();
  const [storedSession, dispatch] = useReducer(ticketResolutionSessionReducer, null);
  const nextSessionId = useRef(0);
  const pendingSessions = useRef(new Set<number>());
  // 路由改变的首帧即隐藏旧表单，随后清理其状态。
  const session = activeResolutionSession(storedSession, scopeKey);
  const ticket = session?.ticket ?? null;
  const isPending = session?.isPending ?? false;

  useEffect(() => {
    dispatch({ type: "scope", scopeKey });
  }, [scopeKey]);

  const refresh = (ticketId: string) =>
    Promise.allSettled([
      queryClient.invalidateQueries({ queryKey: ["tickets", "detail", ticketId] }),
      queryClient.invalidateQueries({ queryKey: ["tickets", "timeline", ticketId] }),
      queryClient.invalidateQueries({ queryKey: ["tickets", "list"] }),
      queryClient.invalidateQueries({ queryKey: ["tickets", "counts"] }),
    ]);

  const mutation = useMutation({
    mutationFn: ({ ticketId, concurrencyStamp, input }: ResolveRequest) => {
      return ticketApi.changeStatus(ticketId, {
        targetStatus: TicketStatus.Resolved,
        concurrencyStamp,
        resolutionResult: input.resolutionResult.trim(),
        feedbackMethod: input.feedbackMethod,
        ...(input.feedbackMethod === "other" ? { feedbackMethodOther: input.feedbackMethodOther?.trim() } : {}),
      });
    },
    onSuccess: (updated, { sessionId }) => {
      queryClient.setQueryData<TicketDetailDto>(["tickets", "detail", updated.id], (current) => (current ? { ...current, ...updated } : current));
      dispatch({ type: "close", sessionId });
      toast.success(t("tickets.detail.resolvedToast"));
      // 写入已成功，后续读取失败不应把成功的解决操作显示成失败。
      void refresh(updated.id);
    },
    onError: async (error, { sessionId, ticketId }) => {
      let latest: TicketDto | undefined;
      if (error instanceof RequestError && error.status === 409) {
        await refresh(ticketId);
        try {
          latest = await ticketApi.get(ticketId);
          queryClient.setQueryData(["tickets", "detail", ticketId], latest);
        } catch {
          // 保留表单；详情暂时不可读取时，用户仍可稍后重试。
        }
      }
      dispatch({ type: "error", sessionId, error, ticket: latest });
    },
    onSettled: (_data, _error, { sessionId }) => {
      pendingSessions.current.delete(sessionId);
      dispatch({ type: "settle", sessionId });
    },
  });

  const errorMessage =
    session?.error instanceof RequestError && session.error.status === 409
      ? t("tickets.resolution.concurrencyError")
      : session?.error instanceof Error
        ? session.error.message
        : session?.error
          ? t("tickets.detail.statusUpdateError")
          : null;

  const open = (nextTicket: TicketDto) => {
    if (session && pendingSessions.current.has(session.id)) return;
    nextSessionId.current += 1;
    dispatch({ type: "open", session: { id: nextSessionId.current, scopeKey, ticket: nextTicket, isPending: false, error: null } });
  };

  return {
    open,
    isPending,
    ticket,
    dialogProps: {
      ticket,
      isPending,
      errorMessage,
      onOpenChange: (nextOpen: boolean) => {
        if (!nextOpen && session && !pendingSessions.current.has(session.id)) dispatch({ type: "close", sessionId: session.id });
      },
      onSubmit: (input: TicketResolutionInput) => {
        if (!session || session.ticket.status !== TicketStatus.Open || pendingSessions.current.has(session.id)) return;
        pendingSessions.current.add(session.id);
        dispatch({ type: "submit", sessionId: session.id });
        mutation.mutate({ sessionId: session.id, ticketId: session.ticket.id, concurrencyStamp: session.ticket.concurrencyStamp, input });
      },
    },
  };
}
